import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  Target,
  CheckCircle,
  AlertCircle,
  Clock,
  Edit,
  Download,
  Star,
  Activity,
  TrendingUp,
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { usePerformanceCycle } from "../../../hooks/usePerformanceCycles";
import { useGoalsByCycle } from "../../../hooks/useGoals";
import { formatDate } from "../../../utils/stringUtils";
import { useCustomAlert } from "../../../hooks/useCustomAlert";
import CustomAlert from "../../../components/common/CustomAlert";
import performanceService from "../../../api/services/performanceService";

const formatDisplayDate = (date) => {
  if (!date) return "N/A";
  return formatDate(date, { includeTime: false });
};

const formatDisplayDateTime = (date) => {
  if (!date) return "N/A";
  return formatDate(date, { includeTime: true });
};

const formatDisplayTime = (date) => {
  if (!date) return "";
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return "";
    return dateObj.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
};

const getGoalStatusColor = (status) => {
  switch (status) {
    case "not_started":
      return "bg-gray-100 text-gray-800";
    case "in_progress":
      return "bg-blue-100 text-blue-800";
    case "completed":
      return "bg-green-100 text-green-800";
    case "overdue":
      return "bg-red-100 text-red-800";
    case "cancelled":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getGoalStatusIcon = (status) => {
  switch (status) {
    case "not_started":
      return <Clock className="h-4 w-4" />;
    case "in_progress":
      return <TrendingUp className="h-4 w-4" />;
    case "completed":
      return <CheckCircle className="h-4 w-4" />;
    case "overdue":
      return <AlertCircle className="h-4 w-4" />;
    default:
      return <Clock className="h-4 w-4" />;
  }
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case "high":
      return "bg-red-100 text-red-800";
    case "medium":
      return "bg-yellow-100 text-yellow-800";
    case "low":
      return "bg-green-100 text-green-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getCycleStatusColor = (status) => {
  switch (status) {
    case "completed":
    case "closed":
      return "text-green-600 bg-green-100";
    case "launched":
    case "in-progress":
    case "active":
      return "text-blue-600 bg-blue-100";
    case "draft":
    case "pending":
      return "text-gray-600 bg-gray-100";
    case "at-risk":
    case "frozen":
      return "text-red-600 bg-red-100";
    default:
      return "text-gray-600 bg-gray-100";
  }
};

const getCycleStatusIcon = (status) => {
  switch (status) {
    case "completed":
    case "closed":
      return <CheckCircle className="h-4 w-4" />;
    case "launched":
    case "in-progress":
    case "active":
      return <Clock className="h-4 w-4" />;
    default:
      return <AlertCircle className="h-4 w-4" />;
  }
};

const PerformanceCycleDetail = ({ cycleId: propCycleId, onClose, isDrawer }) => {
  const { cycleId: paramCycleId } = useParams();
  const cycleId = propCycleId || paramCycleId;
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [isExporting, setIsExporting] = useState(false);
  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();

  const {
    data: cycleData,
    isLoading,
    isError,
  } = usePerformanceCycle(cycleId);

  const {
    data: goalsData,
    isLoading: goalsLoading,
    isError: goalsError,
  } = useGoalsByCycle(cycleId);

  const goals = useMemo(() => {
    if (Array.isArray(goalsData)) return goalsData;
    if (Array.isArray(goalsData?.data)) return goalsData.data;
    return [];
  }, [goalsData]);

  const cycle = useMemo(() => {
    if (!cycleData?.id && !cycleData?._id) return null;

    const totalEmployees =
      cycleData.population?.totalEnrolled ||
      cycleData.population?.totalEligible ||
      0;

    return {
      id: cycleData.id || cycleData._id,
      name: cycleData.cycleName,
      description: cycleData.description,
      startDate: cycleData.startDate,
      endDate: cycleData.endDate,
      status: cycleData.status,
      totalEmployees,
      metrics: cycleData.metrics || {},
    };
  }, [cycleData]);

  const goalStats = useMemo(() => {
    const total = goals.length;
    const completed = goals.filter((g) => g.status === "completed").length;
    const inProgress = goals.filter((g) => g.status === "in_progress").length;
    const notStarted = goals.filter((g) => g.status === "not_started").length;
    const overdue = goals.filter((g) => g.status === "overdue").length;
    const pending = Math.max(0, total - completed);
    const avgProgress =
      total > 0
        ? Math.round(
            goals.reduce((sum, g) => sum + Number(g.progress || 0), 0) / total
          )
        : 0;

    return {
      total,
      completed,
      inProgress,
      notStarted,
      overdue,
      pending,
      avgProgress,
    };
  }, [goals]);

  const departments = useMemo(() => {
    const byDept = {};

    goals.forEach((goal) => {
      const name = goal.employeeDepartment?.trim() || "Unassigned";
      if (!byDept[name]) {
        byDept[name] = {
          name,
          total: 0,
          completed: 0,
          progressSum: 0,
        };
      }
      byDept[name].total += 1;
      byDept[name].progressSum += Number(goal.progress || 0);
      if (goal.status === "completed") {
        byDept[name].completed += 1;
      }
    });

    return Object.values(byDept)
      .map((dept) => ({
        ...dept,
        averageProgress:
          dept.total > 0 ? Math.round(dept.progressSum / dept.total) : 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [goals]);

  const recentActivity = useMemo(() => {
    return [...goals]
      .sort((a, b) => {
        const aTime = new Date(a.updatedAt || a.lastUpdated || 0).getTime();
        const bTime = new Date(b.updatedAt || b.lastUpdated || 0).getTime();
        return bTime - aTime;
      })
      .slice(0, 10)
      .map((goal) => {
        let action = "Goal updated";
        if (goal.status === "completed") action = "Goal completed";
        else if (goal.status === "in_progress") action = "Goal in progress";
        else if (goal.status === "not_started") action = "Goal created";
        else if (goal.status === "overdue") action = "Goal overdue";

        return {
          id: goal.id,
          action,
          title: goal.title,
          employee: goal.employee || "Unknown",
          manager: goal.manager || "N/A",
          progress: Number(goal.progress || 0),
          status: goal.status,
          timestamp: goal.updatedAt || goal.lastUpdated || goal.createdAt,
        };
      });
  }, [goals]);

  const handleBack = () => {
    if (isDrawer) {
      onClose?.();
    } else {
      navigate(-1);
    }
  };

  const handleEditCycle = () => {
    if (isDrawer) {
      const url = new URL(window.location.href);
      url.searchParams.set("action", "editCycle");
      url.searchParams.set("id", cycleId);
      window.history.pushState({}, "", url);
    } else {
      navigate(`/performance_cycles?edit=${cycleId}`);
    }
  };

  const handleExport = async () => {
    if (isExporting || !cycle) return;
    setIsExporting(true);
    try {
      const exportName = cycle.name || "PerformanceCycle";
      const result = await performanceService.exportPerformanceCycle(cycleId, exportName);
      if (result.success) {
        showSuccess("Performance cycle exported successfully");
      } else {
        showError(result.error || "Failed to export performance cycle");
      }
    } catch (err) {
      showError(err.message || "Failed to export performance cycle");
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Error Loading Cycle
        </h3>
        <p className="text-gray-500 mb-4">
          There was an error loading the performance cycle data.
        </p>
        <Button onClick={handleBack} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Cycles
        </Button>
      </div>
    );
  }

  if (!cycle) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Cycle Not Found
        </h3>
        <p className="text-gray-500 mb-4">
          The performance cycle you&apos;re looking for doesn&apos;t exist.
        </p>
        <Button onClick={handleBack} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Cycles
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBack}
            icon={<ArrowLeft className="h-4 w-4" />}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 capitalize">{cycle.name}</h1>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <PermissionGate permission="performance" action="read">
            <Button
              variant="outline"
              icon={<Download className="h-4 w-4" />}
              onClick={handleExport}
              loading={isExporting}
              disabled={isExporting}
            >
              Export
            </Button>
          </PermissionGate>
          <PermissionGate permission="performance" action="update">
            <Button onClick={handleEditCycle} icon={<Edit className="h-4 w-4" />}>
              Edit Cycle
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Status Badge */}
      <div className="flex items-center space-x-2">
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getCycleStatusColor(
            cycle.status
          )}`}
        >
          {getCycleStatusIcon(cycle.status)}
          <span className="ml-2 capitalize">
            {String(cycle.status || "").replace(/_/g, " ")}
          </span>
        </span>
        <span className="text-sm text-gray-500">
          {formatDisplayDate(cycle.startDate)} - {formatDisplayDate(cycle.endDate)}
        </span>
      </div>

      {/* Stats Cards — derived from real goals for this cycle */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Target className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Goals</p>
              <p className="text-2xl font-bold text-gray-900">{goalStats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">
                {goalStats.completed}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">
                {goalStats.pending}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Star className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Progress</p>
              <p className="text-2xl font-bold text-gray-900">
                {goalStats.avgProgress}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {["overview", "departments", "activity", "goals"].map(
            (tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? "border-agrosaf-green text-agrosaf-green"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab}
                {tab === "goals" && goalStats.total > 0 && (
                  <span className="ml-1.5 text-xs text-gray-400">
                    ({goalStats.total})
                  </span>
                )}
              </button>
            )
          )}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === "overview" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Progress Overview
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Overall Goal Progress</span>
                      <span>
                        {goalStats.total > 0
                          ? Math.round(
                              (goalStats.completed / goalStats.total) * 100
                            )
                          : 0}
                        %
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-agrosaf-green h-2 rounded-full"
                        style={{
                          width: `${
                            goalStats.total > 0
                              ? Math.min(
                                  100,
                                  (goalStats.completed / goalStats.total) * 100
                                )
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-green-600">
                        {goalStats.completed}
                      </p>
                      <p className="text-sm text-gray-600">Completed</p>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <Clock className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-yellow-600">
                        {goalStats.pending}
                      </p>
                      <p className="text-sm text-gray-600">Pending</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <TrendingUp className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-blue-600">
                        {goalStats.inProgress}
                      </p>
                      <p className="text-sm text-gray-600">In Progress</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <Users className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-gray-600">
                        {goalStats.notStarted}
                      </p>
                      <p className="text-sm text-gray-600">Not Started</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Recent Activity
                </h3>
                {recentActivity.length > 0 ? (
                  <div className="space-y-3">
                    {recentActivity.slice(0, 5).map((activity) => (
                      <div key={activity.id} className="flex items-start space-x-3">
                        <div className="p-1 bg-blue-100 rounded-full">
                          <Activity className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {activity.action}
                          </p>
                          <p className="text-sm text-gray-700 truncate">
                            {activity.title}
                          </p>
                          <p className="text-sm text-gray-500">
                            {activity.employee} • Manager: {activity.manager}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatDisplayDateTime(activity.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Activity className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      No goal activity yet for this cycle.
                    </p>
                  </div>
                )}
              </div>
            </div>
            {cycle.description && (
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Description
                </h3>
                <p className="text-gray-600 text-sm whitespace-pre-line first-letter:capitalize">
                  {cycle.description}
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === "departments" && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Department Performance
              </h3>
              {departments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Department
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Goals
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Completed
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Avg Progress
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Progress
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {departments.map((dept) => (
                        <tr key={dept.name}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {dept.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {dept.total}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {dept.completed}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <Star className="h-4 w-4 text-yellow-400 mr-1" />
                              {dept.averageProgress}%
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-agrosaf-green h-2 rounded-full"
                                style={{
                                  width: `${
                                    dept.total > 0
                                      ? (dept.completed / dept.total) * 100
                                      : 0
                                  }%`,
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">
                    No department data available. Assign goals to employees with
                    departments to see breakdowns here.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "activity" && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Activity Timeline
                </h3>
                {recentActivity.length > 0 && (
                  <span className="text-sm text-gray-500">
                    {recentActivity.length} recent update
                    {recentActivity.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
              {recentActivity.length > 0 ? (
                <div className="relative space-y-0">
                  {recentActivity.map((activity, index) => {
                    const isLast = index === recentActivity.length - 1;
                    return (
                      <div
                        key={activity.id}
                        className="relative grid grid-cols-[7rem_1.25rem_1fr] gap-x-4"
                      >
                        {/* Date column — fills left side */}
                        <div className="pt-1 text-right">
                          <p className="text-sm font-semibold text-gray-900 leading-tight">
                            {activity.timestamp
                              ? formatDisplayDate(activity.timestamp)
                              : "N/A"}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {formatDisplayTime(activity.timestamp)}
                          </p>
                        </div>

                        {/* Timeline rail */}
                        <div className="relative flex flex-col items-center">
                          <div
                            className={`z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-4 ring-white ${
                              activity.status === "completed"
                                ? "bg-green-500"
                                : activity.status === "overdue"
                                  ? "bg-red-500"
                                  : activity.status === "in_progress"
                                    ? "bg-blue-500"
                                    : "bg-gray-400"
                            }`}
                          >
                            <Activity className="h-3 w-3 text-white" />
                          </div>
                          {!isLast && (
                            <div className="absolute top-5 bottom-0 w-0.5 bg-gray-200" />
                          )}
                        </div>

                        {/* Content card — uses remaining width */}
                        <div
                          className={`min-w-0 rounded-lg border border-gray-200 bg-gray-50/60 p-4 ${
                            isLast ? "mb-0" : "mb-4"
                          }`}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <p className="text-sm font-semibold text-gray-900">
                                  {activity.action}
                                </p>
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getGoalStatusColor(
                                    activity.status
                                  )}`}
                                >
                                  {getGoalStatusIcon(activity.status)}
                                  <span className="ml-1">
                                    {String(activity.status || "").replace(
                                      /_/g,
                                      " "
                                    )}
                                  </span>
                                </span>
                              </div>
                              <p className="text-sm text-gray-700 break-words">
                                {activity.title}
                              </p>
                            </div>
                            <div className="shrink-0 text-right">
                              <p className="text-xs text-gray-500 mb-1">
                                Progress
                              </p>
                              <p className="text-lg font-bold text-gray-900 leading-none">
                                {activity.progress}%
                              </p>
                            </div>
                          </div>

                          <div className="mb-3 h-1.5 w-full rounded-full bg-gray-200">
                            <div
                              className={`h-1.5 rounded-full ${
                                activity.status === "completed"
                                  ? "bg-green-500"
                                  : activity.status === "overdue"
                                    ? "bg-red-500"
                                    : "bg-agrosaf-green"
                              }`}
                              style={{
                                width: `${Math.min(
                                  100,
                                  Math.max(0, activity.progress || 0)
                                )}%`,
                              }}
                            />
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-2 rounded-md bg-white border border-gray-100 px-3 py-2">
                              <Users className="h-4 w-4 text-blue-500 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs text-gray-500">Employee</p>
                                <p className="font-medium text-gray-900 truncate">
                                  {activity.employee}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 rounded-md bg-white border border-gray-100 px-3 py-2">
                              <Star className="h-4 w-4 text-yellow-500 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs text-gray-500">Manager</p>
                                <p className="font-medium text-gray-900 truncate">
                                  {activity.manager}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">
                    No activity recorded for this cycle yet.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "goals" && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Cycle Goals
                </h3>
                {goalStats.total > 0 && (
                  <span className="text-sm text-gray-500">
                    {goalStats.total} goal{goalStats.total !== 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {goalsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green" />
                </div>
              ) : goalsError ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Error Loading Goals
                  </h3>
                  <p className="text-gray-500">
                    Could not load goals for this performance cycle.
                  </p>
                </div>
              ) : goals.length > 0 ? (
                <div className="space-y-4">
                  {goals.map((goal) => {
                    const progress = Number(goal.progress || 0);
                    return (
                      <div
                        key={goal.id}
                        className="p-4 border border-gray-200 rounded-lg hover:border-agrosaf-green/40 transition-colors"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                          <div className="min-w-0 flex-1">
                            <h4 className="font-medium text-gray-900 break-words">
                              {goal.title}
                            </h4>
                            {goal.description && (
                              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                {goal.description}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getGoalStatusColor(
                                goal.status
                              )}`}
                            >
                              {getGoalStatusIcon(goal.status)}
                              <span className="ml-1 capitalize">
                                {String(goal.status || "").replace(/_/g, " ")}
                              </span>
                            </span>
                            {goal.priority && (
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getPriorityColor(
                                  goal.priority
                                )}`}
                              >
                                {goal.priority}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-gray-600 mb-3">
                          <div>
                            <span className="text-gray-500">Employee: </span>
                            <span className="font-medium text-gray-900">
                              {goal.employee || "N/A"}
                            </span>
                            {goal.employeeId && (
                              <span className="block text-xs text-gray-400">
                                ID: {goal.employeeId}
                              </span>
                            )}
                            {goal.employeeDepartment && (
                              <span className="block text-xs text-gray-400">
                                {goal.employeeDepartment}
                              </span>
                            )}
                          </div>
                          <div>
                            <span className="text-gray-500">Manager: </span>
                            <span className="font-medium text-gray-900">
                              {goal.manager || "N/A"}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Start: </span>
                            <span className="font-medium text-gray-900">
                              {formatDisplayDate(goal.startDate)}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">End: </span>
                            <span className="font-medium text-gray-900">
                              {formatDisplayDate(goal.endDate)}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3">
                          <div className="flex-1 bg-gray-200 rounded-full h-2.5">
                            <div
                              className={`h-2.5 rounded-full transition-all ${
                                goal.status === "completed"
                                  ? "bg-green-500"
                                  : goal.status === "overdue"
                                    ? "bg-red-500"
                                    : "bg-agrosaf-green"
                              }`}
                              style={{
                                width: `${Math.min(100, Math.max(0, progress))}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-900 min-w-[40px] text-right">
                            {progress}%
                          </span>
                        </div>

                        {goal.remark && (
                          <p className="mt-3 text-sm text-gray-600 bg-gray-50 rounded-lg p-2 border border-gray-100">
                            <span className="font-medium text-gray-700">
                              Remark:{" "}
                            </span>
                            {goal.remark}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Goals Set
                  </h3>
                  <p className="text-gray-500">
                    No goals have been defined for this performance cycle yet.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Custom Alert */}
      <CustomAlert
        isOpen={alertState.show}
        onClose={hideAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        showCancel={alertState.showCancel}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
      />
    </div>
  );
};

export default PerformanceCycleDetail;
